// Page-level pieces: sections, and the loading, error and not-found states.
// Their words are today's pages'.
import { Component, useEffect, useRef, type ErrorInfo, type ReactNode, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import type { ApiError } from '../api/client'

export function Section({ title, count, actions, children }: { title: string; count?: number; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <SectionHeader title={title} count={count} actions={actions} />
      {children}
    </section>
  )
}

export function SectionHeader({ title, count, actions }: { title: string; count?: number; actions?: ReactNode }) {
  return (
    <header className="section-header">
      <h2>
        {title}
        {count !== undefined && count > 0 && <span className="section-count">{count}</span>}
      </h2>
      {actions}
    </header>
  )
}

export function Loading() {
  return (
    <div className="state-message" aria-busy="true">
      <p className="muted" role="status">
        Loading…
      </p>
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: ApiError | null; onRetry: () => void }) {
  return (
    <div className="state-message" role="alert">
      <h1>The board could not load</h1>
      <p>The board server did not answer as expected.</p>
      {error && (
        <p className="muted">
          {error.message} <code>{error.code}</code>
        </p>
      )}
      <button type="button" className="button button-primary" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}

export function NotFound({ what }: { what: string }) {
  return (
    <div className="state-message">
      <h1>No such {what}</h1>
      <p>
        It may have been archived, or the link is wrong. <Link to="/board">Back to the board</Link>
      </p>
    </div>
  )
}

/** Whether the component is still mounted: an answer that arrives after it left does not move the person. */
export function useMounted(): RefObject<boolean> {
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  return mounted
}

interface PageBoundaryProps {
  /** Changing it (the page's path) clears a caught error. */
  resetKey: string
  children: ReactNode
  onReload?: () => void
}

/**
 * A page that fails while rendering shows a way out instead of a blank
 * window; the rest of the app keeps working. Today's (pages.tsx there),
 * without its report to the engine: the new server takes none.
 */
export class PageBoundary extends Component<PageBoundaryProps, { error: Error | null }> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  componentDidUpdate(previous: PageBoundaryProps) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) this.setState({ error: null })
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('A page failed', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <div className="state-message" role="alert">
        <h1>This page could not be shown</h1>
        <p>Reload to try again; the board may have been updated since this window opened.</p>
        <p className="muted">{error.message}</p>
        <button type="button" className="button button-primary" onClick={() => this.props.onReload ? this.props.onReload() : window.location.reload()}>
          Reload
        </button>
      </div>
    )
  }
}
